import React, { useEffect, useState } from 'react';
import MobileSelect from '../components/MobileSelect.tsx';
import { lookupAPI, profileAPI } from '../services/api.ts';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';

interface DocumentItem {
    id: number;
    title: string;
    createdDate?: string;
    url?: string;
}

const Payslips: React.FC = () => {
    const [documentTypeId, setDocumentTypeId] = useState<string>('');
    const [documentTypes, setDocumentTypes] = useState<{ value: string; label: string }[]>([]);
    const [documents, setDocuments] = useState<DocumentItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        lookupAPI.getDocumentTypes()
            .then(rows => {
                const opts = (rows || []).map((r: any) => ({ value: String(r.id), label: r.name }));
                setDocumentTypes(opts);
                if (opts.length && !documentTypeId) setDocumentTypeId(opts[0].value);
            })
            .catch(() => setDocumentTypes([]));
    }, []);

    useEffect(() => {
        if (!documentTypeId) return;
        setLoading(true);
        setError(null);
        profileAPI.getDocuments(Number(documentTypeId))
            .then((rows: any[]) => {
                const merged = (rows || [])
                    .filter(Boolean)
                    .map((r: any, idx: number) => ({ id: Number(r.id ?? r.Id ?? idx), title: String(r.fileName ?? r.FileName ?? r.title ?? r.Title ?? ''), createdDate: r.createdDate ?? r.CreatedDateTime ?? r.date, url: r.url ?? r.Url }));
                setDocuments(merged);
            })
            .catch((e) => setError(e?.message || 'Failed to load documents'))
            .finally(() => setLoading(false));
    }, [documentTypeId]);

	return (
		<div className="px-4 py-4">
				<div className="flex items-center justify-between mb-3">
					<h2 className="text-lg font-semibold">Documents</h2>
					<div className="flex items-center gap-2">
						<span className="text-xs text-gray-500">Type</span>
						<div className="w-28">
							<MobileSelect
								value={documentTypeId}
								options={documentTypes.length ? documentTypes : [{ value: '1', label: 'Payslip' }]}
								onChange={setDocumentTypeId}
								placeholder="Select"
							/>
						</div>
					</div>
				</div>

            <div className="bg-white rounded border divide-y">
                {documents.map(item => (
                    <button
                        key={item.id}
                        className="w-full text-left px-3 py-3 flex items-center"
                        onClick={async () => {
                            // iOS Safari blocks `window.open()` if it's called after an `await`.
                            // So on web, we open a blank tab synchronously, then navigate it once the blob is ready.
                            const isNative = Capacitor.isNativePlatform();
                            // NOTE: Don't pass `noopener` here — Chrome may return `null` for the WindowProxy.
                            // We'll null-out `opener` ourselves for safety.
                            const popup = !isNative ? window.open('about:blank', '_blank') : null;
                            try {
                                if (popup) popup.opener = null;
                            } catch {
                                // ignore
                            }
                            if (popup && popup.document) {
                                popup.document.title = item.title || 'Document';
                                popup.document.body.innerHTML = '<p style="font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif; padding: 16px;">Loading…</p>';
                            }
                            try {
                                const blob = await profileAPI.downloadDocument(Number(documentTypeId), item.title);
                                
                                // Check if running on native platform (Android/iOS)
                                if (isNative) {
                                    // Convert blob to base64
                                    const reader = new FileReader();
                                    const base64Data = await new Promise<string>((resolve, reject) => {
                                        reader.onloadend = () => {
                                            const base64String = reader.result as string;
                                            // Remove data URL prefix
                                            const base64 = base64String.split(',')[1];
                                            resolve(base64);
                                        };
                                        reader.onerror = reject;
                                        reader.readAsDataURL(blob);
                                    });

                                    // Save PDF to cache directory
                                    const sanitizedFileName = item.title.replace(/[^a-zA-Z0-9.-]/g, '_');
                                    const pdfFileName = sanitizedFileName.toLowerCase().endsWith('.pdf')
                                        ? sanitizedFileName
                                        : `${sanitizedFileName || `document_${item.id}`}.pdf`;
                                    const filePath = `document_${item.id}_${pdfFileName}`;
                                    
                                    await Filesystem.writeFile({
                                        path: filePath,
                                        data: base64Data,
                                        directory: Directory.Cache,
                                    });

                                    // Get the file URI
                                    const fileUri = await Filesystem.getUri({
                                        path: filePath,
                                        directory: Directory.Cache,
                                    });

                                    // Try to share/open the PDF with external app
                                    try {
                                        await Share.share({
                                            title: item.title,
                                            url: fileUri.uri,
                                            dialogTitle: 'Open Document',
                                        });
                                    } catch (shareError: any) {
                                        // If share fails, try opening with intent (Android)
                                        if (Capacitor.getPlatform() === 'android') {
                                            window.open(fileUri.uri, '_system');
                                        } else {
                                            throw shareError;
                                        }
                                    }
                                } else {
                                    // Web platform - use blob URL
                                    const blobUrl = URL.createObjectURL(blob);
                                    if (popup && !popup.closed) {
                                        popup.location.href = blobUrl;
                                    } else {
                                        // If popup was blocked, try opening now (works on most desktop browsers)
                                        const opened = window.open(blobUrl, '_blank');
                                        if (!opened) {
                                            // Last resort: same-tab navigation
                                            window.location.href = blobUrl;
                                        }
                                    }
                                    // Give Safari time to load the blob before revoking.
                                    setTimeout(() => URL.revokeObjectURL(blobUrl), 10 * 60_000);
                                }
                            } catch (e: any) {
                                if (popup && !popup.closed) popup.close();
                                alert(e?.message || 'Failed to open document');
                            }
                        }}
                    >
						<span className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mr-3">
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h10M7 11h10M7 15h7"/>
							</svg>
						</span>
						<div className="flex-1">
                            <div className="font-medium">{item.title}</div>
                            {item.createdDate && (<div className="text-xs text-gray-500">{new Date(item.createdDate).toLocaleDateString()}</div>)}
						</div>
						<svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
						</svg>
                    </button>
				))}
                {loading && (
                    <div className="p-4 text-center">
                        <div className="inline-block animate-spin h-6 w-6 border-4 border-primary border-t-transparent rounded-full"></div>
                        <p className="mt-2 text-sm text-gray-600">Loading...</p>
                    </div>
                )}
                {error && <div className="p-4 text-sm text-red-600">{error}</div>}
                {!loading && !error && documents.length === 0 && (
                    <div className="p-4 text-sm text-gray-500">No documents found.</div>
                )}
			</div>
		</div>
	);
};

export default Payslips;


